<template>
	<div class="p4">
		<!-- Render fields -->
		<div v-if="!isLoading">
			<!-- Display form based on schema -->
			<div v-for="field in computedSchema" class="mb6">
				<core-form-input
					:name="field.label"
					:componentName="field.component"
					:label="field.label"
					v-model="form[field.bindTo]"
					v-bind="field.props"
				/>
			</div>

			<!-- Stakeholders -->
			<aeg-transaction-stakeholders
				:transactionId="transactionId"
				class="mt6"
			/>

			<!-- Button -->
			<div class="xs-12 flex xs-between">
				<j-button @click="updateTransaction" :isLoading="isUpdateLoading">
					Update
				</j-button>
				<j-button
					:isLoading="isDeleteLoading"
					@click="deleteTransaction"
					type="error"
					>Delete</j-button
				>
			</div>
		</div>

		<!-- Loading -->
		<j-spinner v-else />
	</div>
</template>

<script>
/**
 * This component is responsible for update of case
 * It's a smart component that does API calls
 */
import CoreFormInput from "@/modules/core/components/CoreFormInput"
import JSpinner from "@/components/JSpinner"
import JButton from "@/components/JButton"
import AegTransactionStakeholders from "./AegTransactionStakeholders"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

export default {
	components: {
		CoreFormInput,
		JSpinner,
		JButton,
		AegTransactionStakeholders
	},
	props: {
		policyId: {
			type: String,
			required: true
		},

		transactionId: {
			type: String,
			required: true
		},

		schema: {
			type: Array,
			default: () => []
		}
	},

	data() {
		return {
			cache: null,
			form: {},

			isLoading: false,
			isUpdateLoading: false,
			isDeleteLoading: false
		}
	},

	computed: {
		// Append model into schema
		computedSchema() {
			return this.schema.map(item => ({
				...item,
				// last part of bindTo
				model: item?.bindTo?.split(".")?.slice(-1)?.[0] || ""
			}))
		}
	},

	methods: {
		async fetchTransaction() {
			this.form = {}
			this.cache = null

			const query = gql`
				query transaction($where: policyWhereInput) {
					policies(where: $where) {
						list {
							id
							transactions {
								id
								amount
								currencyCode
								dateTime
								dueDateTime
								postDateTime
								method
								status
								facts {
									id
									type
									value {
										booleanValue
										dateValue
										numberValue
										stringValue
									}
								}
							}
						}
					}
				}
			`

			this.isLoading = true
			const res = await api({
				query,
				variables: { where: { id: this.policyId } }
			})
			this.isLoading = false

			// Populate form
			this.cache = res?.data?.policies?.list?.[0]?.transactions?.find(
				item => item.id === this.transactionId
			)
			this.populateFormFromCache()
		},

		/**
		 * This method goes through the cache and populates the form accordingly
		 * based on the schema definition
		 */
		populateFormFromCache() {
			this.computedSchema.forEach(field => {
				// Facts
				if (field.bindTo.includes("transaction.facts")) {
					this.$set(
						this.form,
						field.bindTo,
						this.cache?.facts?.find(item => item.type === field.model)?.value?.[
							field.scalarDataType
						]
					)
					return
				}

				// Rest
				this.$set(this.form, field.bindTo, this.cache?.[field.model])
			})
		},

		async updateTransaction(field) {
			this.isUpdateLoading = true

			// Collect facts from schema
			const factFields = this.computedSchema.filter(item =>
				item.bindTo.includes("transaction.facts")
			)

			// Collect root fields from schema
			const rootFields = this.computedSchema.filter(
				item => !item.bindTo.includes("transaction.facts")
			)

			// Populate values for root fields for update
			const rootFieldsWithValues = {}
			rootFields.forEach(item => {
				rootFieldsWithValues[item.model] = this.form[item.bindTo]
			})

			// Update all things via batch calls
			const res = await Promise.all([
				// Root fields
				this.apiUpdateTransaction(this.transactionId, {
					...rootFieldsWithValues
				}),
				// Fact fields
				...factFields.map(field =>
					this.apiUpdateTransactionFact(
						this.transactionId,
						this?.cache?.facts?.find(c => c.type === field.model)?.id,
						{
							type: field.model,
							value: {
								[field.scalarDataType || "stringValue"]: this.form[field.bindTo]
							}
						}
					)
				)
			])

			// If errors, tell user it was updated partially and they have to check the transaction
			if (
				(res?.data?.updateTransaction &&
					res?.data?.updateTransaction?.status !== "success") ||
				(res?.data?.updateTransactionFact &&
					res?.data?.updateTransactionFact?.status !== "success")
			) {
				this.$store.dispatch("showError", [
					"Transaction was partially updated, please update it again."
				])
			}

			this.$emit("transaction:updated")
			this.isUpdateLoading = false
		},

		async apiUpdateTransactionFact(transactionId, factId, input) {
			const query = gql`
				mutation updateTransactionFact(
					$transactionId: String!
					$factId: String!
					$input: updateFactInput!
				) {
					updateTransactionFact(
						transactionId: $transactionId
						factId: $factId
						input: $input
					) {
						status
						errors
					}
				}
			`

			const res = await api({
				query,
				variables: {
					factId,
					transactionId,
					input
				}
			})

			return Promise.resolve(res)
		},

		async apiUpdateTransaction(transactionId, input) {
			const query = gql`
				mutation updateTransaction(
					$transactionId: String!
					$input: updateTransactionInput!
				) {
					updateTransaction(transactionId: $transactionId, input: $input) {
						status
						errors
					}
				}
			`

			const res = await api({
				query,
				variables: {
					transactionId,
					input
				}
			})

			return Promise.resolve(res)
		},

		async deleteTransaction() {
			const query = gql`
				mutation deleteTransaction($transactionId: String!) {
					deleteTransaction(transactionId: $transactionId) {
						status
						errors
					}
				}
			`

			this.isDeleteLoading = true
			const res = await api({
				query,
				variables: { transactionId: this.transactionId }
			})
			this.isDeleteLoading = false

			// Check errors
			if (res?.data?.deleteTransaction?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.deleteTransaction?.errors?.[0] || "There was an error"
				)
				return
			}

			this.$emit("transaction:deleted")
		}
	},

	created() {
		this.fetchTransaction()
	}
}
</script>
